import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import axiosInstance from "../../utils/Axios";
import { useState } from "react";
import { useFormContext } from "../context/Context";
import { Alert, Snackbar, Tooltip, Box, Paper } from "@mui/material";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    overflow: "hidden",
    maxWidth: "420px",
    width: "100%",
  },
}));

const GradientHeader = styled(Box)({
  background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
  padding: "32px 24px",
  textAlign: "center",
  position: "relative",
});

const SuccessIconWrapper = styled(Box)({
  width: "80px",
  height: "80px",
  borderRadius: "50%",
  background: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
  animation: "pulse 2s ease-in-out infinite",
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255,255,255,0.4)" },
    "50%": { transform: "scale(1.05)", boxShadow: "0 0 0 15px rgba(255,255,255,0)" },
    "100%": { transform: "scale(1)", boxShadow: "0 0 0 0 rgba(255,255,255,0)" },
  },
});

const DetailCard = styled(Paper)({
  padding: "12px 16px",
  marginBottom: "12px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8f9fa",
  border: "1px solid #e9ecef",
  transition: "all 0.2s ease",
  "&:hover": {
    background: "#e9ecef",
    borderColor: "#dee2e6",
  },
});

const MakePayment = () => {
  const { accountNumber, phone, totalAmount } = useFormContext();
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [copiedText, setCopiedText] = useState("");

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const makeStudentPayment = async () => {
    try {
      const formData = new FormData();
      formData.append("account_number", accountNumber);
      formData.append("phone_number", phone);
      formData.append("amount", totalAmount);

      await axiosInstance.post(
        "/request_stk_push/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccess(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    window.location.reload();
  };

  React.useEffect(() => {
    if (success && totalAmount) {
      setOpen(true);
    }
  }, [success, totalAmount]);

  return (
    <div className="makePayment_container">
      {totalAmount ? (
        success ? (
          <StyledDialog
            onClose={handleClose}
            aria-labelledby="payment-success-dialog"
            open={open}
          >
            <GradientHeader>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: 12,
                  color: "rgba(255,255,255,0.8)",
                  "&:hover": {
                    color: "#fff",
                    background: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              <SuccessIconWrapper>
                <CheckCircleIcon sx={{ fontSize: 48, color: "#fff" }} />
              </SuccessIconWrapper>

              <Typography
                variant="h5"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  marginBottom: "4px",
                }}
              >
                Payment Initiated!
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.85)" }}
              >
                M-Pesa prompt sent to your phone
              </Typography>
            </GradientHeader>

            <DialogContent sx={{ padding: "24px", background: "#fff" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  marginBottom: "20px",
                  textAlign: "center",
                  lineHeight: 1.6,
                }}
              >
                Complete the payment on your phone. If you didn't receive the prompt,
                use the details below:
              </Typography>

              {/* Account Number Card */}
              <DetailCard elevation={0}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #635bff 0%, #4f46e5 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AccountBalanceWalletIcon sx={{ color: "#fff", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#888", fontSize: "11px" }}>
                      ACCOUNT NUMBER
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#333" }}>
                      {accountNumber}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(accountNumber, "Account Number")}
                    sx={{ color: "#635bff" }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </DetailCard>

              {/* Paybill Card */}
              <DetailCard elevation={0}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ReceiptLongIcon sx={{ color: "#fff", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#888", fontSize: "11px" }}>
                      PAYBILL NUMBER
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: "#333" }}>
                      827774
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard("827774", "Paybill")}
                    sx={{ color: "#4caf50" }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </DetailCard>

              {/* Total Amount Card */}
              <DetailCard
                elevation={0}
                sx={{
                  background: "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)",
                  border: "1px solid #ffe082",
                  marginBottom: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PaymentsIcon sx={{ color: "#fff", fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#888", fontSize: "11px" }}>
                      TOTAL AMOUNT
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#e65100" }}>
                      Ksh. {totalAmount?.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </DetailCard>
            </DialogContent>

            <DialogActions
              sx={{
                padding: "16px 24px 24px",
                background: "#fff",
              }}
            >
              <Button
                fullWidth
                onClick={handleClose}
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
                  borderRadius: "10px",
                  padding: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "15px",
                  boxShadow: "0 4px 14px rgba(76, 175, 80, 0.4)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #43a047 0%, #2e7d32 100%)",
                    boxShadow: "0 6px 20px rgba(76, 175, 80, 0.5)",
                  },
                }}
              >
                Done
              </Button>
            </DialogActions>
          </StyledDialog>
        ) : (
          <button
            className="button-style button-success"
            onClick={makeStudentPayment}
            variant="outlined"
          >
            Make Payment
          </button>
        )
      ) : null}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{
            width: "100%",
            borderRadius: "10px",
            fontWeight: 500,
          }}
        >
          {copiedText} copied!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default MakePayment;


